import { AsyncButton, loadWrapper } from "aic";
import { AppSettingsContext } from "contexts/AppSettingsContext";
import { HierarchyContext } from "contexts/HierarchyContext";
import {
  ILoadClinicalLibraryParams,
  loadClinicalLibrary,
} from "functions/api/hierarchy/loadClinicalLibrary";
import { IRequestStatus } from "functions/api/interface";
import { ReactNode, useContext, useState } from "react";
import { Container, Form, Modal, Row } from "react-bootstrap";

export interface LoadXmlLibraryModalProps {
  show: boolean;
  onClose: () => void;
}

/**
 * Modal for uploading and importing a clinical library XML file into the
 * current hierarchy. Validates the XML before sending it to the backend.
 *
 * @param props - Destructured parameter object
 * @param props.show - Whether the modal is visible.
 * @param props.onClose - Callback invoked to close the modal.
 * @returns A modal ReactNode for XML upload and processing.
 */
export const LoadXmlLibraryModal = ({ show, onClose }: LoadXmlLibraryModalProps): ReactNode => {
  const [xmlFile, setXmlFile] = useState<File | null>(null);
  const appSettings = useContext(AppSettingsContext);
  const hierarchyContext = useContext(HierarchyContext);
  const hierarchyId = hierarchyContext?.hierarchy?.id;
  const [loadRequest, setLoadRequest] = useState<IRequestStatus<ILoadClinicalLibraryParams>>({
    requesting: false,
    error: false,
  });

  /**
   * Closes the modal and resets state.
   */
  const cx = () => {
    setXmlFile(null);
    setLoadRequest({
      requesting: false,
      error: false,
    });
    onClose();
  };

  /**
   * Handles loading the XML file: reads the file, validates it, and if valid,
   *
   * @param xmlFile - The XML file to be processed.
   */
  const onLoad = async (xmlFile: File) => {
    const text = await xmlFile.text();
    /* istanbul ignore else */
    if (hierarchyId && text.length > 0) {
      const parser = new DOMParser();
      const xmlDoc = parser.parseFromString(text, "text/xml");
      const parseError = xmlDoc.querySelector("parsererror");
      if (parseError) {
        setLoadRequest({
          requesting: false,
          error: true,
          errorText: "Invalid XML file. Please upload a well-formed XML.",
        });
      } else {
        await loadWrapper(
          loadClinicalLibrary,
          { hierarchyId, xmlText: text },
          loadRequest,
          setLoadRequest,
          appSettings.backEndUrl,
        );
      }
    }
  };

  return (
    <Modal
      show={show}
      size="lg"
    >
      <Modal.Header
        closeButton
        onHide={cx}
      >
        <Modal.Title>
          <Container>Upload clinical library XML file</Container>
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Container>
          <Row className="m-2">
            <h5 className="mb-2">Select a file</h5>
            <Form.Group controlId="upload-xml">
              <Form.Control
                aria-label="XML file upload"
                className="mb-2"
                type="file"
                accept=".xml"
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  const files = e.target.files;
                  /* istanbul ignore else */
                  if (files && files.length > 0) {
                    setXmlFile(files[0]);
                  }
                }}
              />
            </Form.Group>
          </Row>
        </Container>
      </Modal.Body>
      <Modal.Footer>
        {loadRequest.requestedId?.hierarchyId === hierarchyId && (
          <h6 className="text-success">File processed successfully</h6>
        )}
        <AsyncButton
          id="load-xml-button"
          className="mt-2"
          disabled={!xmlFile}
          requestStatus={loadRequest}
          onClick={() => onLoad(xmlFile!)}
        >
          Load
        </AsyncButton>
        <AsyncButton
          id="loadxmllibrarymodal-cancel"
          variant="secondary"
          onClick={cx}
        >
          Cancel
        </AsyncButton>
      </Modal.Footer>
    </Modal>
  );
};

LoadXmlLibraryModal.displayName = "LoadXmlLibraryModal";
